'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface RailwaySustainabilityProps {
  currentLanguage: string;
}

const RailwaySustainability = ({ currentLanguage }: RailwaySustainabilityProps) => {
  const [selectedCountry, setSelectedCountry] = useState(0);

  const content = {
    en: {
      title: 'Railway Sustainability Excellence',
      subtitle: 'International best practices from Japan and Switzerland',
      countries: [
        {
          name: 'Japan',
          flag: '🇯🇵',
          achievements: [
            'Shinkansen energy efficiency: 85% improvement',
            'Zero direct emissions on electric lines',
            'Regenerative braking systems',
            'Smart energy management'
          ],
          practices: [
            'High-speed rail optimization',
            'Station energy conservation',
            'Modal shift strategies',
            'Carbon footprint tracking'
          ]
        },
        {
          name: 'Switzerland',
          flag: '🇨🇭',
          achievements: [
            '90% renewable energy powered',
            'Alpine tunnel efficiency',
            'Integrated public transport',
            'Modal shift leadership'
          ],
          practices: [
            'Hydropower integration',
            'Network optimization',
            'Freight-passenger balance',
            'Environmental reporting'
          ]
        }
      ],
      solutions: [
        {
          title: 'Energy Efficiency Optimization',
          icon: 'BoltIcon',
          description: 'Reduce energy consumption through smart systems and regenerative technologies'
        },
        {
          title: 'Emission Reduction Strategies',
          icon: 'CloudIcon',
          description: 'Transition to electric and hydrogen-powered trains with renewable energy'
        },
        {
          title: 'Infrastructure Upgrades',
          icon: 'WrenchScrewdriverIcon',
          description: 'Modernize tracks, stations, and signaling for optimal performance'
        },
        {
          title: 'Performance Benchmarking',
          icon: 'ChartBarIcon',
          description: 'Compare against international standards and track progress'
        }
      ]
    },
    hi: {
      title: 'रेलवे स्थिरता उत्कृष्टता',
      subtitle: 'जापान और स्विट्जरलैंड से अंतरराष्ट्रीय सर्वोत्तम प्रथाएं',
      countries: [
        {
          name: 'जापान',
          flag: '🇯🇵',
          achievements: [
            'शिन्कानसेन ऊर्जा दक्षता: 85% सुधार',
            'बिजली की लाइनों पर शून्य प्रत्यक्ष उत्सर्जन',
            'पुनर्योजी ब्रेकिंग सिस्टम',
            'स्मार्ट ऊर्जा प्रबंधन'
          ],
          practices: [
            'हाई-स्पीड रेल अनुकूलन',
            'स्टेशन ऊर्जा संरक्षण',
            'मोडल शिफ्ट रणनीतियां',
            'कार्बन फुटप्रिंट ट्रैकिंग'
          ]
        },
        {
          name: 'स्विट्जरलैंड',
          flag: '🇨🇭',
          achievements: [
            '90% नवीकरणीय ऊर्जा संचालित',
            'अल्पाइन सुरंग दक्षता',
            'एकीकृत सार्वजनिक परिवहन',
            'मोडल शिफ्ट नेतृत्व'
          ],
          practices: [
            'जलविद्युत एकीकरण',
            'नेटवर्क अनुकूलन',
            'माल-यात्री संतुलन',
            'पर्यावरण रिपोर्टिंग'
          ]
        }
      ],
      solutions: [
        {
          title: 'ऊर्जा दक्षता अनुकूलन',
          icon: 'BoltIcon',
          description: 'स्मार्ट सिस्टम और पुनर्योजी प्रौद्योगिकियों के माध्यम से ऊर्जा खपत कम करें'
        },
        {
          title: 'उत्सर्जन में कमी की रणनीतियां',
          icon: 'CloudIcon',
          description: 'नवीकरणीय ऊर्जा के साथ इलेक्ट्रिक और हाइड्रोजन-संचालित ट्रेनों में संक्रमण'
        },
        {
          title: 'बुनियादी ढांचा उन्नयन',
          icon: 'WrenchScrewdriverIcon',
          description: 'इष्टतम प्रदर्शन के लिए ट्रैक, स्टेशन और संकेत आधुनिकीकरण'
        },
        {
          title: 'प्रदर्शन बेंचमार्किंग',
          icon: 'ChartBarIcon',
          description: 'अंतरराष्ट्रीय मानकों के विरुद्ध तुलना करें और प्रगति ट्रैक करें'
        }
      ]
    },
    ar: {
      title: 'التميز في استدامة السكك الحديدية',
      subtitle: 'أفضل الممارسات الدولية من اليابان وسويسرا',
      countries: [
        {
          name: 'اليابان',
          flag: '🇯🇵',
          achievements: [
            'كفاءة طاقة شينكانسن: تحسين بنسبة 85%',
            'صفر انبعاثات مباشرة على الخطوط الكهربائية',
            'أنظمة الكبح التجديدية',
            'إدارة الطاقة الذكية'
          ],
          practices: [
            'تحسين السكك الحديدية فائقة السرعة',
            'حفظ طاقة المحطات',
            'استراتيجيات التحول النمطي',
            'تتبع البصمة الكربونية'
          ]
        },
        {
          name: 'سويسرا',
          flag: '🇨🇭',
          achievements: [
            '90% من الطاقة المتجددة',
            'كفاءة نفق جبال الألب',
            'النقل العام المتكامل',
            'قيادة التحول النمطي'
          ],
          practices: [
            'تكامل الطاقة الكهرومائية',
            'تحسين الشبكة',
            'توازن الشحن والركاب',
            'التقارير البيئية'
          ]
        }
      ],
      solutions: [
        {
          title: 'تحسين كفاءة الطاقة',
          icon: 'BoltIcon',
          description: 'تقليل استهلاك الطاقة من خلال الأنظمة الذكية والتقنيات التجديدية'
        },
        {
          title: 'استراتيجيات خفض الانبعاثات',
          icon: 'CloudIcon',
          description: 'الانتقال إلى القطارات الكهربائية والهيدروجينية بالطاقة المتجددة'
        },
        {
          title: 'تحديث البنية التحتية',
          icon: 'WrenchScrewdriverIcon',
          description: 'تحديث المسارات والمحطات والإشارات للأداء الأمثل'
        },
        {
          title: 'قياس الأداء',
          icon: 'ChartBarIcon',
          description: 'المقارنة مع المعايير الدولية وتتبع التقدم'
        }
      ]
    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;
  const currentCountry = currentContent.countries[selectedCountry];

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Country Selector */}
        <div className="flex gap-4 mb-8 justify-center">
          {currentContent.countries.map((country, index) => (
            <button
              key={index}
              onClick={() => setSelectedCountry(index)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                selectedCountry === index
                  ? 'bg-primary text-primary-foreground shadow-natural scale-105'
                  : 'bg-card text-text-secondary hover:bg-card/80 border border-border'
              }`}
            >
              <div className="flex items-center space-x-2">
                <span className="text-2xl">{country.flag}</span>
                <span>{country.name}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Country Details */}
        <div className="bg-card border border-border rounded-xl p-8 shadow-natural mb-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center">
                <Icon name="TrophyIcon" size={24} className="text-primary mr-2" />
                Key Achievements
              </h3>
              <ul className="space-y-3">
                {currentCountry.achievements.map((achievement, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-text-secondary">{achievement}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center">
                <Icon name="LightBulbIcon" size={24} className="text-primary mr-2" />
                Best Practices
              </h3>
              <ul className="space-y-3">
                {currentCountry.practices.map((practice, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-text-secondary">{practice}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Solutions */}
        <div>
          <h3 className="font-heading font-bold text-2xl text-text-primary mb-8 text-center">
            Our Railway Sustainability Solutions
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {currentContent.solutions.map((solution, index) => (
              <div 
                key={index}
                className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 hover:shadow-natural transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={solution.icon as any} size={24} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-lg text-text-primary mb-2">
                      {solution.title}
                    </h4>
                    <p className="text-text-secondary text-sm">
                      {solution.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RailwaySustainability;